// @version: 1.0.1
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DndDropEvent } from 'ngx-drag-drop';
import { filter } from 'rxjs/operators';
import { FileuploadService } from './fileupload.service';
import { IsRequiredService } from './is-required.service';
import { FIELUPLOAD_ERROR, FILE_LIST_POSITION, IFileResult, TRANSLATE_TEXTE, ITranslateText, UPLOAD_STATE } from './fileupload.model';
import { TRANSLATE_PATH } from '../../shared.model';


@Component({
   selector: 'app-fileupload',
   templateUrl: './fileupload.component.html',
   providers: [
      {
         provide: NG_VALIDATORS,
         multi: true,
         useExisting: FileuploadComponent
      }
   ],
   standalone: false
})

export class FileuploadComponent implements OnInit, Validator, OnDestroy {
   public FIELUPLOAD_ERROR = FIELUPLOAD_ERROR;
   public TRANSLATE_PATH = TRANSLATE_PATH;

   public touched: boolean = false;
   public allArray: { [key: string]: boolean[] } = {};
   public content: ITranslateText = {} as ITranslateText;
   private readonly_: boolean = false; 

   private allowedExtensions_: string[] = ['pdf','docx'];
   
   @Input() public maxNumberFiles: number = 5;
   @Input() public translatePath: string = "";
   @Input() public actMaxNumberOfFiles: number = 5;
   @Input() public maxFilesize: number = 5 * 1024 * 1024;
   @Input() public position: string = FILE_LIST_POSITION.BOTTOM; 
   @Input() public overlay: boolean = false; 
   @Input() public lfnr: number = -1;
   @Input() dataUrlSave: IFileResult[] = [];
   @Input() 
   set allowedExtensions(value: string[]) {
      this.allowedExtensions_ = value ?? ['pdf', 'docx'];
      this.setTranslateTexte();
   };
   get allowedExtensions (): string[] {
      return Array.isArray(this.allowedExtensions_) ? this.allowedExtensions_ : [];
   }

   @Input() 
   set readonly (value: boolean) {
      this.readonly_ = value; 
   }

   get readonly (): boolean {
      return this.readonly_; 
   }

   @Output() enabledOutput: EventEmitter<boolean> = new EventEmitter<boolean>();
   @Output() dataUrlSaveChange: EventEmitter<IFileResult[]> = new EventEmitter<IFileResult[]>();

   get disabled (): boolean {
      return this.sFileupload.state === UPLOAD_STATE.DISABLED;
   }

   get valid (): boolean {
      return this.sFileupload.state.toString().indexOf('ERROR') == -1;
   }

   get state (): UPLOAD_STATE {
      return this.sFileupload.state;
   }

   constructor (private sTranslate: TranslateService, public sIsRequired: IsRequiredService, public sFileupload: FileuploadService) {
      this.sFileupload.initFiles(this.dataUrlSave);
   }

   ngOnInit (): void {
      this.initContent();
      this.setTranslateTexte();
   }

   ngOnDestroy(): void {
      this.touched = false;
      this.allowedExtensions_ = ['pdf', 'docx'];
      this.translatePath = "";
      this.actMaxNumberOfFiles = 5;
      this.maxFilesize = 5 * 1024 * 1024;
      this.position = FILE_LIST_POSITION.BOTTOM; 
      this.overlay = false; 
      this.lfnr = -1;
      this.allArray = {};
      this.dataUrlSave = [];

      this.sFileupload.reset();
   }

   public validate (control: AbstractControl): ValidationErrors | null {
      if (control.hasError('required')) this.sFileupload.state = UPLOAD_STATE.REQUIRED;
      switch(this.state) {
         case UPLOAD_STATE.DISABLED: return null;
         case UPLOAD_STATE.EMPTY || UPLOAD_STATE.VALID: return null;
         case UPLOAD_STATE.ERROR_MAX: return { AmountToBig: true };
         case UPLOAD_STATE.ERROR_NO_CHOICE: return { NoChoice: true };
         case UPLOAD_STATE.ERROR_SIZE: return { MaxSize: true };
         case UPLOAD_STATE.ERROR_TYPE: return { FileType: true };
         case UPLOAD_STATE.ERROR_INVALID_FILENAME: return {};
         default: return null;
      }
   }

   private onChange = (files: IFileResult[]) => {};
   private onTouched = () => {};
   private onValidatorChange = () => {}; 
   registerOnValidatorChange?(fn: () => void): void {
      throw new Error('Method not implemented.');
   }

   
   private initContent (): void {
      this.content = {
         title: "Dokumente uploaden",
         amountToBig: `Sie dürfen nur die erlaubte Anzahl von ${this.actMaxNumberOfFiles} übertragen`,
         noChoice: "Sie haben keine Datei ausgewählt",
         maxSize: `Bitte die Dateigröße von 5Mb einhalten`,
         fileType: `Bitte nur erlaubte Dateiformate (${this.allowedExtensions.join(';')})`,
         required: "Es muss mindestens eine Dateien ausgewählt werden ",
         button: "Fenster Öffnen",
         icon: "cloud_upload",
         filenameIsInvalid: "Der Dateiname wurde umbenannt. Klammern und Leerzeichen wurden entfernt."
      }
   }

   public isError (error: string): boolean {
      switch(error) {
         case FIELUPLOAD_ERROR.AMOUNTTOBIG:
            return this.state === UPLOAD_STATE.ERROR_MAX;
         case FIELUPLOAD_ERROR.NOCHOICE:
            return this.state === UPLOAD_STATE.ERROR_NO_CHOICE;
         case FIELUPLOAD_ERROR.MAXSIZE: 
            return this.state === UPLOAD_STATE.ERROR_SIZE;
         case FIELUPLOAD_ERROR.FILETYPE:
            return this.state === UPLOAD_STATE.ERROR_TYPE;
         case FIELUPLOAD_ERROR.FILENAMEISINVALID:
            return this.state === UPLOAD_STATE.ERROR_INVALID_FILENAME;
         case FIELUPLOAD_ERROR.REQUIRED:
            return this.state === UPLOAD_STATE.REQUIRED;
         default:
            return false;
      }
   }

   private setTranslateTexte() {
      this.content.amountToBig = `Sie dürfen nur die erlaubte Anzahl von ${this.actMaxNumberOfFiles} übertragen`;
      this.content.maxSize = `Bitte die Dateigröße von ${this.sFileupload.formatBytes(this.maxFilesize)} einhalten`;
      this.content.fileType = `Bitte nur erlaubte Dateiformate (${this.allowedExtensions.join(';')})`;

      if (this.translatePath) {
         this.setTranslatedText(TRANSLATE_TEXTE.BUTTON);
         this.setTranslatedText(TRANSLATE_TEXTE.ICON);
         this.setTranslatedText(TRANSLATE_TEXTE.TITLE);
         this.setTranslatedText(TRANSLATE_TEXTE.ERROR_AMOUNTTOBIG);
         this.setTranslatedText(TRANSLATE_TEXTE.ERROR_NOCHOICE);
         this.setTranslatedText(TRANSLATE_TEXTE.ERROR_MAXSIZE);
         this.setTranslatedText(TRANSLATE_TEXTE.ERROR_FILETYPE);
         this.setTranslatedText(TRANSLATE_TEXTE.ERROR_REQUIRED);
         this.setTranslatedText(TRANSLATE_TEXTE.ERROR_FILENAMEISINVALID);
      }
   }

   private setTranslatedText (type: string) {
      this.sTranslate.get(this.translatePath + '.' + type).pipe(
         filter(value => value != this.translatePath + '.' + type)
      )
      .subscribe(value => {
         const key: (keyof ITranslateText) = <keyof ITranslateText>type;
         this.content[key] = value;  
      })
   }

   private markAsTouched () {
      if (!this.touched) {
         this.onTouched();
         this.touched = true;
      }
   }

   public addFile (event: Event): void {
      this.markAsTouched();
      event.preventDefault();
      const fileList: FileList = (event.target as HTMLInputElement).files!;
      this.processFiles(fileList);
   }

   /**
    * Wenn win file über die fläche gezogen wird
    * @param event 
   */
   public onDrop (event: DndDropEvent): void { 
      this.markAsTouched();
      this.processFiles(event.event.dataTransfer!.files!);
   }

   /** Remove File mit Müllereimer */
   public removeFile (index: number) {
      this.dataUrlSave.splice(this.dataUrlSave.indexOf(this.dataUrlSave[index]), 1);
      
      this.markAsTouched();
      this.onChange(this.dataUrlSave);
      this.onValidatorChange();

      this.sFileupload.resetState();
   }

   private async processFiles (fileList: FileList): Promise<void> {
      const files = Array.from(fileList);

     this.sFileupload.resetState();
      if (files.length <= 0) {
         this.sFileupload.state = UPLOAD_STATE.ERROR_NO_CHOICE;
         this.onValidatorChange();
         return;
      }

      if (!this.valid) {
         this.onValidatorChange();
         return;
      }

      if (!this.sFileupload.checkFileExtensionTest(files, this.allowedExtensions) && ! this.sFileupload.checkMaxFileSizeTest(files, this.maxFilesize) && !this.sFileupload.checkErrorMax(files, this.actMaxNumberOfFiles, this.dataUrlSave.length)) {
         const dataUrl: IFileResult[] = await Promise.all(files.map(f => {
          
            return this.toFileResult(f);
         }));
   
         dataUrl.forEach(url => {
            this.dataUrlSave.push(url);
         })

         this.onChange(this.dataUrlSave);
         this.dataUrlSaveChange.emit(this.dataUrlSave);
         this.dataUrlSave = []
      }
   }

   private toFileResult (file: File): Promise<IFileResult> {
      return new Promise<IFileResult>((resolve, reject) => {
         const fileReader: FileReader = new FileReader();

         fileReader.onloadend = async () => {
            if (fileReader.result instanceof ArrayBuffer) {
               reject();
               return;
            }

            file = await this.sFileupload.checkValidFileName(file,<string>fileReader.result)
            const fileResult: IFileResult = {
               filename: file.name,
               file: (fileReader.result!.substring(fileReader.result!.search("base64") + 7)!), 
               fileBlob: file,
               size: file.size,
               fileExtension: file!.name!.split('.')!.pop()!,
               lfnr: this.lfnr,
               openFile: function() {
                  //change DataURL strings to blob
                  const fileBlob = file;
                  const fileURL = URL.createObjectURL(fileBlob);
                  const link = document.createElement('a');
                  link.href = fileURL;
                  link.download =  `${file.name}`;
                  link.click();
               }
            };
            resolve(fileResult);
         }
    
         fileReader.readAsDataURL(file);
      });
   }

   public onClickClose () {
      this.enabledOutput.emit(false);
   }
}
